import toast from 'react-hot-toast';

import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import {
  extractDevDetails,
  extractErrorMessage,
  extractRequestName,
  extractStatusCode,
  isSkippableError,
} from '../error-utils';

export interface RequestRetryInfo {
  /** Whether this is a retry attempt (not the first request) */
  isRetry?: boolean;
  /** Whether this is the final attempt (no more retries) */
  isFinalAttempt?: boolean;
  /** Optional request ID for deduplication */
  requestId?: string;
}

/**
 * Show an error toast when an API error occurs during impersonation.
 *
 * This provides feedback to developers testing in lower environments
 * that an API error occurred, likely due to impersonation configuration.
 *
 * Skips toast for:
 * - Aborted requests
 * - Offline/network errors
 * - Non-final retry attempts (to avoid duplicate toasts)
 *
 * @param error The error that occurred
 * @param context Optional context about the request (retry status, etc.)
 */
export const showImpersonationErrorToast = (
  error: unknown,
  context?: RequestRetryInfo,
): void => {
  // Skip toast for skippable errors
  if (isSkippableError(error)) {
    console.debug('[Impersonation] Skipping toast for skippable error:', error);
    return;
  }

  // Skip toast for retry attempts that aren't final
  if (context?.isRetry && !context.isFinalAttempt) {
    console.debug('[Impersonation] Skipping toast for non-final retry attempt');
    return;
  }

  // Extract error details
  const statusCode = extractStatusCode(error);
  const message = extractErrorMessage(error);
  const devDetails = extractDevDetails(error);
  const requestName = extractRequestName(error);

  // Log detailed info for developers
  console.warn('[Impersonation Error]', devDetails);

  // Build display message
  const displayMessage = statusCode ? `${statusCode}: ${message}` : message;

  // Build title with request name if available
  const title = requestName
    ? `Impersonation Error: ${requestName}`
    : 'Impersonation Error';

  // Show toast - stays until dismissed (no auto-timeout)
  toast.custom(
    toastObj => (
      <ToastMessageBlock id={toastObj.id} severity="error" title={title}>
        {displayMessage}
      </ToastMessageBlock>
    ),
    { duration: 1000 * 5 },
  );
};
